import {
  type OfferData,
  OfferCardTypeEnum,
} from '@wwe-portal/entities/offer.type';
import { type Banner, maxItemsByType } from '@wwe-portal/ui/features/offer';

import type { StoreContentProps } from '../hooks';
import type { StoreItemVirtualRow } from '../virtual-row/virtual-row';
import {
  getRowPaddingBottom,
  VirtualRowEnum,
} from '../virtual-row/virtual-row';
import { groupBy, groupNItems } from '@wwe-portal/ui/lib/group-by';
import { tryGetOfferCardType } from '@wwe-portal/ui/features/offer/lib/mapper';

const flattenOffers = (
  rows: StoreItemVirtualRow[],
  {
    offers,
    getBanner,
    maxColumns,
  }: {
    offers: OfferData[];
    maxColumns: number;
    getBanner: () => Banner | undefined;
  }
) => {
  const items: {
    offer?: OfferData;
    banner?: Banner;
    type: OfferCardTypeEnum;
  }[] = [];
  const banner = getBanner();
  if (banner)
    items.push({
      banner,
      type: OfferCardTypeEnum.SINGLE1,
    });
  items.push(
    ...offers.map((offer) => ({ offer, type: tryGetOfferCardType(offer) }))
  );

  const groups = groupNItems(
    groupBy(
      items,
      (offer) => offer.type,
      (type) => maxItemsByType[type]
    ),
    maxColumns
  );

  const virtualRows = groups.map((groupItem) => {
    const groupItemChildren = groupItem.children.flatMap(
      (item) => item.children
    );
    const offersInGroup = groupItemChildren
      .filter((item) => item.offer != null)
      .map((item) => item.offer);

    const foundBanner = groupItemChildren.find((item) => item.banner)?.banner;
    const row: StoreItemVirtualRow = {
      type: VirtualRowEnum.OfferGridRow,
      offerRow: {
        offers: offersInGroup as OfferData[],
        banner: foundBanner,
      },
    };
    return row;
  });
  rows.push(...virtualRows);
};

export const getNumberColumns = ({
  sm,
  xl3,
}: {
  sm: boolean | undefined;
  xl3: boolean | undefined;
}) => {
  if (xl3) return 3;
  if (sm) return 2;
  return 1;
};

const addSiblingFlag = (rows: StoreItemVirtualRow[]) => {
  rows.forEach((row, index) => {
    const prevItem = rows[index - 1];
    const nextItem = rows[index + 1];
    if (prevItem) {
      row.prevRowType = prevItem.type;
    }
    if (nextItem) {
      row.nextRowType = nextItem.type;
    }
  });
};

const genGetBanner = (banner: Banner | undefined) => {
  let bannerAdded = false as boolean;
  return () => {
    if (bannerAdded || !banner) {
      return undefined;
    }
    bannerAdded = true;
    return banner;
  };
};

export const flattenContent = (
  content: StoreContentProps['content'],
  responsive: { sm: boolean | undefined; xl3: boolean | undefined }
) => {
  const maxColumns = getNumberColumns(responsive);
  const rows: StoreItemVirtualRow[] = [];
  const categoryIndexes: Record<string, number> = {};

  for (const contentItem of content) {
    const getBanner = genGetBanner(contentItem.banner);
    rows.push({
      categorySummary: {
        id: contentItem.categoryId,
        title: contentItem.categoryTitle,
        viewAs: contentItem.viewAs,
      },
      type: VirtualRowEnum.CategorySummary,
    });
    categoryIndexes[contentItem.categoryId] = rows.length - 1;

    // Insert banner if any
    if (contentItem.banner) {
      flattenOffers(rows, {
        offers: [],
        getBanner,
        maxColumns,
      });
    }

    // Insert offers if any
    if (contentItem.offers) {
      flattenOffers(rows, {
        offers: contentItem.offers,
        getBanner,
        maxColumns,
      });
    }

    if (contentItem.sections != null) {
      for (const section of contentItem.sections) {
        rows.push({
          type: VirtualRowEnum.SectionSummary,
          sectionSummary: {
            title: section.title,
            description: section.description,
            tooltipTitle: section.tooltipTitle,
            sectionImage: section.sectionImage,
          },
        });

        flattenOffers(rows, {
          offers: section.offers,
          getBanner,
          maxColumns,
        });
      }
    }
  }
  addSiblingFlag(rows);

  return { rows, categoryIndexes };
};

const LARGE_SCREEN_CONTENT_PADDING = 580;
const SMALL_SCREEN_CONTENT_PADDING = 40;
const COLUMN_GAP = 12;
const DEFAULT_WIDTH = 348;
const DEFAULT_HEIGHT = 167;
const estimateSizeByType = {
  [VirtualRowEnum.OfferGridRow]: 528,
  [VirtualRowEnum.CategorySummary]: 44,
  [VirtualRowEnum.SectionSummary]: 125,
};

export const getEstimatedSizes = (
  rows: StoreItemVirtualRow[],
  {
    width,
    xl3,
    lg,
    sm,
  }: {
    width: number;
    lg: boolean | undefined;
    sm: boolean | undefined;
    xl3: boolean | undefined;
  }
) => {
  const containerPadding = lg
    ? LARGE_SCREEN_CONTENT_PADDING
    : SMALL_SCREEN_CONTENT_PADDING;

  const maxColumns = getNumberColumns({ sm, xl3 });

  const estimateSize = (row: StoreItemVirtualRow) => {
    if (row.type === VirtualRowEnum.OfferGridRow) {
      let result =
        ((width - containerPadding - (maxColumns - 1) * COLUMN_GAP) *
          DEFAULT_HEIGHT) /
        DEFAULT_WIDTH;
      const paddingBottom = getRowPaddingBottom(row.type, row.nextRowType);
      result += paddingBottom;
      return Math.max(0, result);
    }
    return estimateSizeByType[row.type];
  };
  return rows.map(estimateSize);
};
